/**
 * Definición declarativa del menú de navegación.
 *
 * Replica EXACTAMENTE el orden y la visibilidad por permiso/rol/sede del sidebar
 * del SSR (`templates/base.html`; resumen en `progress/explore_pantallas_ssr.md`,
 * sección a). Cada entrada lleva su predicado `visible`; `useNavSections` las
 * filtra contra la sesión (`docs/frontend_architecture.md`, Pr. 5: el menú no
 * inventa navegación ni permisos que el SSR no tenga).
 *
 * Correspondencia con el SSR (de arriba abajo):
 *  - Inicio: incondicional.
 *  - Nueva orden: `has_perm('add_orders')`.
 *  - Órdenes (Pendientes/En proceso/Entregadas/Anuladas/Buscar): incondicional.
 *  - Operación → Horario: solo rol `operario`.
 *  - Talonarios: `has_perm('manage_talonarios')`.
 *  - Equipo → Usuarios: `has_perm('manage_users')`.
 *  - Admin (Sedes/Atendidos/Roles/Configuración/Precios cotizador): `has_perm('all')`.
 *  - Análisis (Estadísticas/Actividad): `has_perm('advanced_stats')`.
 *  - Inventario: `sede_inv_activo`; "Activar por sede" además solo rol `admin`.
 *  - Herramientas → Cotizador `use_cotizador`, Optimizador `use_cutlist`,
 *    Pedidos de láminas `view_pedidos_laminas` (cada uno con su permiso).
 *  - Horarios → Horario láser `sede_laser_activo`; Horario semanal rol != `operario`.
 */
import { ROUTES } from "./routes";
import type { NavSection, NavVisibilityContext } from "./types";

const isOperario = (ctx: NavVisibilityContext) => ctx.role === "operario";
const isAdmin = (ctx: NavVisibilityContext) => ctx.role === "admin";
// "No operario" exige un rol presente: sin sesión hidratada (rol indefinido) la
// entrada no se muestra. Evita que el menú pinte entradas en el estado de carga.
const isNonOperario = (ctx: NavVisibilityContext) =>
  ctx.role !== undefined && ctx.role !== "operario";
const can = (perm: string) => (ctx: NavVisibilityContext) => ctx.hasPermission(perm);

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "inicio",
    title: "",
    items: [
      { id: "dashboard", label: "Inicio", to: ROUTES.dashboard, icon: "fas fa-home" },
      {
        id: "orders-new",
        label: "Nueva orden",
        to: ROUTES.ordersNew,
        icon: "fas fa-plus",
        visible: can("add_orders"),
      },
    ],
  },
  {
    id: "ordenes",
    title: "Órdenes",
    items: [
      { id: "orders-pending", label: "Pendientes", to: ROUTES.ordersPending, icon: "fas fa-clock" },
      { id: "orders-in-process", label: "En proceso", to: ROUTES.ordersInProcess, icon: "fas fa-cogs" },
      { id: "orders-delivered", label: "Entregadas", to: ROUTES.ordersDelivered, icon: "fas fa-truck" },
      { id: "orders-cancelled", label: "Anuladas", to: ROUTES.ordersCancelled, icon: "fas fa-ban" },
      { id: "orders-search", label: "Buscar", to: ROUTES.ordersSearch, icon: "fas fa-search" },
    ],
  },
  {
    id: "operacion",
    title: "Operación",
    items: [
      // En el SSR el operario ve "Horario" en Operación; el resto lo ve al final.
      { id: "schedule-op", label: "Horario", to: ROUTES.schedule, icon: "fas fa-calendar", visible: isOperario },
    ],
  },
  {
    id: "talonarios",
    title: "Talonarios",
    items: [
      {
        id: "talonarios",
        label: "Talonarios",
        to: ROUTES.talonarios,
        icon: "fas fa-clipboard-check",
        visible: can("manage_talonarios"),
      },
    ],
  },
  {
    id: "equipo",
    title: "Equipo",
    items: [
      {
        id: "users",
        label: "Usuarios",
        to: ROUTES.users,
        icon: "fas fa-users",
        visible: can("manage_users"),
      },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    items: [
      { id: "sedes", label: "Sedes", to: ROUTES.sedes, icon: "fas fa-building", visible: can("all") },
      { id: "atendidos", label: "Atendidos", to: ROUTES.atendidos, icon: "fas fa-user-tie", visible: can("all") },
      // Paridad con el SSR: todo el bloque Admin (incluido Roles) está bajo
      // `has_perm('all')` en `base.html`. El gating de acceso de la PANTALLA de
      // roles es `manage_roles` (la ruta/endpoint lo exige), pero la VISIBILIDAD
      // del enlace replica el menú del SSR (`all`); no se inventa otra navegación.
      { id: "roles", label: "Roles", to: ROUTES.roles, icon: "fas fa-user-shield", visible: can("all") },
      {
        id: "settings",
        label: "Configuración",
        to: ROUTES.settings,
        icon: "fas fa-cog",
        visible: can("all"),
      },
      {
        id: "cotizador-precios",
        label: "Precios cotizador",
        to: ROUTES.cotizadorPrecios,
        icon: "fas fa-dollar-sign",
        visible: can("all"),
      },
    ],
  },
  {
    id: "analisis",
    title: "Análisis",
    items: [
      {
        id: "advanced-stats",
        label: "Estadísticas avanzadas",
        to: ROUTES.advancedStats,
        icon: "fas fa-chart-line",
        visible: can("advanced_stats"),
      },
      {
        id: "user-activity",
        label: "Actividad usuarios",
        to: ROUTES.userActivity,
        icon: "fas fa-eye",
        visible: can("advanced_stats"),
      },
    ],
  },
  {
    id: "inventario",
    title: "Inventario",
    items: [
      {
        id: "inventario",
        label: "Inventario",
        to: ROUTES.inventario,
        icon: "fas fa-boxes-stacked",
        visible: (ctx) => ctx.sedeInvActivo,
      },
      {
        id: "inventario-admin",
        label: "Activar por sede",
        to: ROUTES.inventarioAdmin,
        icon: "fas fa-toggle-on",
        visible: (ctx) => ctx.sedeInvActivo && isAdmin(ctx),
      },
    ],
  },
  {
    id: "herramientas",
    title: "Herramientas",
    items: [
      {
        id: "cotizador",
        label: "Cotizador de puertas",
        to: ROUTES.cotizador,
        icon: "fas fa-calculator",
        visible: can("use_cotizador"),
      },
      {
        id: "cutlist",
        label: "Optimizador de cortes",
        to: ROUTES.cutlist,
        icon: "fas fa-table-cells-large",
        visible: can("use_cutlist"),
      },
      {
        id: "pedidos-laminas",
        label: "Pedidos de láminas",
        to: ROUTES.pedidosLaminas,
        icon: "fas fa-layer-group",
        visible: can("view_pedidos_laminas"),
      },
    ],
  },
  {
    id: "horarios",
    title: "Horarios",
    items: [
      {
        id: "laser-schedule",
        label: "Horario láser",
        to: ROUTES.laserSchedule,
        icon: "fas fa-bolt",
        visible: (ctx) => ctx.sedeLaserActivo,
      },
      {
        id: "schedule-week",
        label: "Horario semanal",
        to: ROUTES.schedule,
        icon: "fas fa-calendar",
        // En el SSR todos menos el operario lo ven al final (el operario lo tiene
        // arriba, en Operación). Exige rol presente: no se pinta sin sesión.
        visible: isNonOperario,
      },
    ],
  },
];
